import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import {
  LOG_PAGE,
  isWaiting,
  personName,
  refusalDetails,
  refusalMessage,
  type AssignableClient,
  type DelegatedPerson,
  type DelegationLogEntry,
  type DelegationRole,
  type Tier,
  type UserAdminDelegation,
} from "./delegation";

/** What the person dialog is doing. */
export type PersonMode = "create" | "approve" | "change-role" | "reactivate";

/**
 * No password, deliberately (operator ruling, 2026-11-01): a people
 * administrator never sets one. The person claims the account by signing in
 * with Microsoft under this e-mail address; only an administrator sets a
 * password, in User Management.
 */
export interface PersonForm {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isValidated: boolean;
}

const emptyPersonForm = (): PersonForm => ({
  firstName: "",
  lastName: "",
  email: "",
  role: "",
  isValidated: true,
});

/** A row of `staff-client-assignments` as the clients dialog reads it. */
export interface PersonAssignment {
  id: number;
  userId: number;
  clientId: string;
  confirmed: boolean;
  clientName: string | null;
  clientCode: string | null;
}

/** A user row as the administrator's "make a people administrator" picker reads it. */
interface UserRowLike {
  id: number | string;
  email?: string;
  firstName?: string;
  lastName?: string;
  isValidated?: boolean;
  adminUser?: boolean;
  tier?: Tier | null;
  role?: string;
}

interface RoleRowLike {
  id: string;
  name?: string;
  tier?: Tier;
  deletedAt?: string | null;
}

interface RawService {
  create(data: object): Promise<unknown>;
  patch(id: number | string, data: object): Promise<unknown>;
  remove(id: number | string): Promise<unknown>;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RANK: Record<Tier, number> = { viewer: 1, operator: 2, admin: 3 };

/**
 * Administration → People.
 *
 * TWO FACES, one route:
 *   - a PEOPLE ADMINISTRATOR (a delegate) administers the people below them:
 *     add (with no password: the person signs in with Microsoft), approve a
 *     Microsoft sign-in, change a role, unlock, deactivate / reactivate, hand
 *     out and take back clients — only the actions the API's
 *     `delegated-people` list says its guard accepts. Never a password;
 *   - an ADMINISTRATOR grants and revokes people administration, chooses each
 *     delegate's roles, and reads every delegated change.
 *
 * Every write goes through the RAW Feathers service, which rejects: the
 * ServiceStores resolve a refusal as null, and a refusal here must reach the
 * dialog in the API's own words. The rows themselves stay in the ServiceStores
 * (`delegated-people`, `user-admin-delegations`, `user-admin-delegation-log`,
 * `staff-client-assignments`); this store only derives over them.
 */
class PeopleStore {
  rootStore: RootStore;

  loading = false;
  loadError: string | null = null;
  search = "";
  /** The People list or the log could not be read: the delegate's failure, kept apart. */
  logError: string | null = null;

  // ---- the person dialog ----
  personMode: PersonMode | null = null;
  personTargetId: number | null = null;
  personForm: PersonForm = emptyPersonForm();
  personSaving = false;
  personError: string | null = null;
  personErrorDetails: string[] = [];

  // ---- deactivate ----
  deactivateTargetId: number | null = null;
  deactivating = false;
  deactivateError: string | null = null;

  // ---- clients ----
  clientsTargetId: number | null = null;
  clientsLoading = false;
  clientsError: string | null = null;
  clientToAdd = "";
  clientBusy = false;

  // ---- inline row action (unlock) ----
  rowBusyId: number | null = null;
  rowError: string | null = null;
  /** The last thing that worked, said once above the list. */
  notice: string | null = null;

  // ---- administrator: grant / edit / revoke ----
  delegationEditingId: number | null = null;
  delegationUserId: number | null = null;
  delegationRoleIds: string[] = [];
  delegationSaving = false;
  delegationError: string | null = null;
  revokeTargetId: number | null = null;
  revoking = false;
  revokeError: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- stores and services ----

  private store(key: string): ServiceStoreType | null {
    return ((this.rootStore as unknown as Record<string, unknown>)?.[key] as ServiceStoreType | undefined) ?? null;
  }

  private raw(path: string): RawService {
    const app = (this.rootStore as unknown as { app?: { service?: (p: string) => RawService } })?.app;
    const service = app?.service?.(path);
    if (!service) throw new Error("Not connected to the API — reload and try again.");
    return service;
  }

  get currentUserId(): number | null {
    const id = (this.rootStore as unknown as { authStore?: { currentUser?: { id?: unknown } } })?.authStore
      ?.currentUser?.id;
    const n = Number(id);
    return id == null || !Number.isInteger(n) ? null : n;
  }

  get isAdmin(): boolean {
    return (this.rootStore as unknown as { authStore?: { isAdminUser?: boolean } })?.authStore?.isAdminUser === true;
  }

  // ---- derived ----

  get delegations(): UserAdminDelegation[] {
    return ((this.store("userAdminDelegationsStore")?.entities ?? []) as unknown as UserAdminDelegation[])
      .slice()
      .sort((a, b) => (a.userName ?? "").localeCompare(b.userName ?? ""));
  }

  /** The signed-in delegate's own delegation (null for an administrator or anyone else). */
  get myDelegation(): UserAdminDelegation | null {
    const me = this.currentUserId;
    return me == null ? null : (this.delegations.find((d) => Number(d.userId) === me) ?? null);
  }

  get isDelegate(): boolean {
    return !this.isAdmin && !!this.myDelegation;
  }

  /** The delegation is stored but grants nothing right now (the API says why). */
  get myLapse(): string | null {
    const d = this.myDelegation;
    return d && d.status === "lapsed" ? (d.lapseMessage ?? "Your people administration is paused.") : null;
  }

  /** Roles the delegate may grant right now (the API marks each). */
  get grantableRoles(): DelegationRole[] {
    return (this.myDelegation?.allowedRoles ?? []).filter((r) => r.grantable && !!r.name);
  }

  /**
   * "Add person" is offered only when the API says this delegate may create an
   * account (`createsAccounts`: in force, and this HQ has Microsoft sign-in —
   * the only way the person can claim an account with no password) and there
   * is a role to give it. Unknown reads as no.
   */
  get canAddPeople(): boolean {
    return this.isDelegate && this.myDelegation?.createsAccounts === true && this.grantableRoles.length > 0;
  }

  get assignableClients(): AssignableClient[] {
    return this.myDelegation?.assignableClients ?? [];
  }

  get people(): DelegatedPerson[] {
    return (this.store("delegatedPeopleStore")?.entities ?? []) as unknown as DelegatedPerson[];
  }

  get visiblePeople(): DelegatedPerson[] {
    const term = this.search.trim().toLowerCase();
    const rows = this.people.slice().sort((a, b) => {
      const w = Number(isWaiting(b)) - Number(isWaiting(a));
      return w !== 0 ? w : personName(a).localeCompare(personName(b));
    });
    if (!term) return rows;
    return rows.filter((p) =>
      [p.firstName, p.lastName, p.email, p.role, p.department].filter(Boolean).join(" ").toLowerCase().includes(term),
    );
  }

  get waitingCount(): number {
    return this.people.filter(isWaiting).length;
  }

  get logEntries(): DelegationLogEntry[] {
    return ((this.store("userAdminDelegationLogStore")?.entities ?? []) as unknown as DelegationLogEntry[])
      .slice()
      .sort((a, b) => b.id - a.id);
  }

  /** The log came back full, so older entries exist beyond it. */
  get logTruncated(): boolean {
    return this.logEntries.length >= LOG_PAGE;
  }

  findPerson(id: number | null): DelegatedPerson | null {
    return id == null ? null : (this.people.find((p) => p.id === id) ?? null);
  }

  setSearch(value: string) {
    this.search = value;
  }

  clearNotice() {
    this.notice = null;
  }

  // ---- loading ----

  /** Load the face this person sees. Never rejects: failures land on `loadError`. */
  async load(): Promise<void> {
    const ended = sessionMark(this.rootStore);
    runInAction(() => {
      this.loading = true;
      this.loadError = null;
      this.logError = null;
    });
    try {
      if (this.isAdmin) {
        await Promise.all([
          findOrThrow(this.store("userAdminDelegationsStore"), { query: {} }),
          // The pickers for "make someone a people administrator".
          findOrThrow(this.store("userStore"), { query: { $limit: 500 } }),
          findOrThrow(this.store("rolesStore"), { query: { $limit: 200 } }),
        ]);
      } else {
        await findOrThrow(this.store("userAdminDelegationsStore"), { query: {} });
        if (ended()) return;
        // A lapsed delegation grants nothing, and `delegated-people` refuses it — say why instead.
        if (this.myDelegation && !this.myLapse) {
          await findOrThrow(this.store("delegatedPeopleStore"), { query: {} });
        }
      }
    } catch (err) {
      if (ended() || isSessionEnded(err)) return;
      runInAction(() => {
        this.loadError = loadErrorMessage(err, "People could not be loaded.");
      });
    }
    try {
      await findOrThrow(this.store("userAdminDelegationLogStore"), { query: { $limit: LOG_PAGE } });
    } catch (err) {
      if (!ended() && !isSessionEnded(err)) {
        runInAction(() => {
          this.logError = loadErrorMessage(err, "The record of changes could not be loaded.");
        });
      }
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  private async refreshAfterWrite(): Promise<void> {
    try {
      await Promise.all([
        this.isAdmin ? Promise.resolve() : findOrThrow(this.store("delegatedPeopleStore"), { query: {} }),
        findOrThrow(this.store("userAdminDelegationsStore"), { query: {} }),
        findOrThrow(this.store("userAdminDelegationLogStore"), { query: { $limit: LOG_PAGE } }),
      ]);
    } catch (err) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.loadError = loadErrorMessage(err, "The change was made, but the list could not be refreshed.");
      });
    }
  }

  // ---- the person dialog: create / approve / change role / reactivate ----

  private openPerson(mode: PersonMode, person: DelegatedPerson | null) {
    this.personMode = mode;
    this.personTargetId = person?.id ?? null;
    const role = person?.role ?? "";
    this.personForm = {
      ...emptyPersonForm(),
      firstName: person?.firstName ?? "",
      lastName: person?.lastName ?? "",
      email: person?.email ?? "",
      // An approval CHOOSES the role (a Microsoft sign-up holds the column's
      // default only because it needs one), so it starts empty — as on User
      // Management. A reactivation keeps the current role when it is grantable.
      role:
        mode === "approve" || mode === "create"
          ? ""
          : this.grantableRoles.some((r) => r.name?.toLowerCase() === role.toLowerCase()) && mode === "reactivate"
            ? role
            : "",
    };
    this.personError = null;
    this.personErrorDetails = [];
  }

  startCreate() {
    this.openPerson("create", null);
  }
  startApprove(person: DelegatedPerson) {
    this.openPerson("approve", person);
  }
  startChangeRole(person: DelegatedPerson) {
    this.openPerson("change-role", person);
  }
  startReactivate(person: DelegatedPerson) {
    this.openPerson("reactivate", person);
  }

  get personTarget(): DelegatedPerson | null {
    return this.findPerson(this.personTargetId);
  }

  setPersonField<K extends keyof PersonForm>(field: K, value: PersonForm[K]) {
    this.personForm = { ...this.personForm, [field]: value };
  }

  /** The role choices the dialog offers: only what the delegate may grant. */
  get roleChoices(): string[] {
    return this.grantableRoles.map((r) => r.name as string);
  }

  get isPersonFormValid(): boolean {
    const f = this.personForm;
    if (this.personSaving || !this.personMode) return false;
    if (!this.roleChoices.includes(f.role)) return false;
    if (this.personMode !== "create") return true;
    return EMAIL.test(f.email.trim()) && !!f.firstName.trim() && !!f.lastName.trim();
  }

  /** The body each mode sends — exactly the fields the API lets a delegate send. */
  get personPayload(): Record<string, unknown> {
    const f = this.personForm;
    switch (this.personMode) {
      case "create":
        return {
          firstName: f.firstName.trim(),
          lastName: f.lastName.trim(),
          email: f.email.trim().toLowerCase(),
          role: f.role,
          isValidated: f.isValidated,
        };
      case "approve":
      case "reactivate":
        return { isValidated: true, role: f.role };
      case "change-role":
        return { role: f.role };
      default:
        return {};
    }
  }

  async savePerson(): Promise<boolean> {
    if (!this.isPersonFormValid) {
      this.personError = "Fill in every field and choose one of the roles you may grant.";
      return false;
    }
    const mode = this.personMode;
    const target = this.personTarget;
    this.personSaving = true;
    this.personError = null;
    this.personErrorDetails = [];
    try {
      const user = this.raw("user");
      if (mode === "create") await user.create(this.personPayload);
      else if (target) await user.patch(target.id, this.personPayload);
      else throw new Error("That person is no longer in your list. Reload and try again.");
      const name = mode === "create" ? `${this.personForm.firstName.trim()} ${this.personForm.lastName.trim()}` : personName(target ?? {});
      const email = this.personForm.email.trim().toLowerCase();
      runInAction(() => {
        this.notice =
          mode === "create"
            ? `Created ${name}'s account with the role "${this.personForm.role}". They sign in with Microsoft as ${email}; HQ set no password.`
            : mode === "change-role"
              ? `${name}'s role is now "${this.personForm.role}".`
              : `${name} can sign in now, with the role "${this.personForm.role}".`;
        this.personMode = null;
        this.personTargetId = null;
        this.personForm = emptyPersonForm();
      });
      await this.refreshAfterWrite();
      return true;
    } catch (err) {
      runInAction(() => {
        if (isSessionEnded(err)) return;
        this.personError = refusalMessage(err, "The change was not made.");
        this.personErrorDetails = refusalDetails(err);
      });
      return false;
    } finally {
      runInAction(() => {
        this.personSaving = false;
      });
    }
  }

  // ---- deactivate ----

  get deactivateTarget(): DelegatedPerson | null {
    return this.findPerson(this.deactivateTargetId);
  }

  startDeactivate(person: DelegatedPerson) {
    this.deactivateTargetId = person.id;
    this.deactivateError = null;
  }

  async submitDeactivate(): Promise<boolean> {
    const target = this.deactivateTarget;
    if (!target) return false;
    this.deactivating = true;
    this.deactivateError = null;
    try {
      await this.raw("user").patch(target.id, { isValidated: false });
      runInAction(() => {
        // What the API did before it answered: every session the account held
        // was ended (its sockets closed, its refresh tokens revoked) and every
        // further request is refused.
        this.notice = `${personName(target)} is signed out of HQ everywhere and can no longer sign in.`;
        this.deactivateTargetId = null;
      });
      await this.refreshAfterWrite();
      return true;
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.deactivateError = refusalMessage(err, "The account was not deactivated.");
      });
      return false;
    } finally {
      runInAction(() => {
        this.deactivating = false;
      });
    }
  }

  // ---- unlock (inline) ----

  async unlock(person: DelegatedPerson): Promise<boolean> {
    this.rowBusyId = person.id;
    this.rowError = null;
    try {
      await this.raw("user").patch(person.id, { lockedUntil: null, failedLoginAttempts: 0 });
      runInAction(() => {
        this.notice = `${personName(person)} can try signing in again.`;
      });
      await this.refreshAfterWrite();
      return true;
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.rowError = refusalMessage(err, "The sign-in was not unlocked.");
      });
      return false;
    } finally {
      runInAction(() => {
        this.rowBusyId = null;
      });
    }
  }

  // ---- clients ----

  get clientsTarget(): DelegatedPerson | null {
    return this.findPerson(this.clientsTargetId);
  }

  /** The person's assignments, from the ServiceStore the dialog loaded. */
  get targetAssignments(): PersonAssignment[] {
    const id = this.clientsTargetId;
    if (id == null) return [];
    return ((this.store("staffClientAssignmentsStore")?.entities ?? []) as unknown as PersonAssignment[])
      .filter((a) => Number(a.userId) === id)
      .sort((a, b) => (a.clientName ?? "").localeCompare(b.clientName ?? ""));
  }

  /**
   * Whether the delegate may take this assignment away: only for a client they
   * hold themselves (the API's guard refuses the rest — an administrator's
   * grant of another client stays an administrator's to remove).
   */
  mayRemove(assignment: PersonAssignment): boolean {
    const id = assignment.clientId.toLowerCase();
    return this.assignableClients.some((c) => c.clientId.toLowerCase() === id);
  }

  /** Clients the delegate holds that this person does not have yet. */
  get clientsToOffer(): AssignableClient[] {
    const has = new Set(this.targetAssignments.map((a) => a.clientId.toLowerCase()));
    return this.assignableClients.filter((c) => !has.has(c.clientId.toLowerCase()));
  }

  async startClients(person: DelegatedPerson): Promise<void> {
    this.clientsTargetId = person.id;
    this.clientToAdd = "";
    this.clientsError = null;
    await this.loadClients();
  }

  private async loadClients(): Promise<void> {
    const id = this.clientsTargetId;
    if (id == null) return;
    runInAction(() => {
      this.clientsLoading = true;
      this.clientsError = null;
    });
    try {
      await findOrThrow(this.store("staffClientAssignmentsStore"), { query: { userId: id } });
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.clientsError = loadErrorMessage(err, "Their clients could not be loaded.");
      });
    } finally {
      runInAction(() => {
        this.clientsLoading = false;
      });
    }
  }

  setClientToAdd(clientId: string) {
    this.clientToAdd = clientId;
  }

  async addClient(): Promise<boolean> {
    const target = this.clientsTarget;
    if (!target || !this.clientToAdd) return false;
    this.clientBusy = true;
    this.clientsError = null;
    try {
      await this.raw("staff-client-assignments").create({ userId: target.id, clientId: this.clientToAdd });
      runInAction(() => {
        this.clientToAdd = "";
      });
      await this.loadClients();
      await this.refreshAfterWrite();
      return true;
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.clientsError = refusalMessage(err, "The client was not added.");
      });
      return false;
    } finally {
      runInAction(() => {
        this.clientBusy = false;
      });
    }
  }

  async removeAssignment(assignment: PersonAssignment): Promise<boolean> {
    this.clientBusy = true;
    this.clientsError = null;
    try {
      await this.raw("staff-client-assignments").remove(assignment.id);
      await this.loadClients();
      await this.refreshAfterWrite();
      return true;
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.clientsError = refusalMessage(err, "The client was not removed.");
      });
      return false;
    } finally {
      runInAction(() => {
        this.clientBusy = false;
      });
    }
  }

  // ---- administrator: grant, edit, revoke ----

  /** Live, non-admin, operator-or-above staff who are not people administrators yet. */
  get delegateCandidates(): UserRowLike[] {
    const taken = new Set(this.delegations.map((d) => Number(d.userId)));
    return ((this.store("userStore")?.entities ?? []) as unknown as UserRowLike[])
      .filter((u) => u.isValidated === true && u.adminUser !== true && u.tier === "operator" && !taken.has(Number(u.id)))
      .sort((a, b) => personName(a as never).localeCompare(personName(b as never)));
  }

  /** The tier the person being given delegation holds (edit: theirs; grant: the picked person's). */
  get delegationTargetTier(): Tier | null {
    if (this.delegationEditingId != null) {
      return this.delegations.find((d) => d.id === this.delegationEditingId)?.delegateTier ?? null;
    }
    const user = ((this.store("userStore")?.entities ?? []) as unknown as UserRowLike[]).find(
      (u) => Number(u.id) === this.delegationUserId,
    );
    return (user?.tier as Tier | undefined) ?? null;
  }

  /**
   * The roles an administrator may allow: every live role, each marked whether
   * it sits below the person's tier (the API refuses one that does not).
   */
  get roleOptions(): Array<{ id: string; name: string; tier: Tier; allowed: boolean }> {
    const tier = this.delegationTargetTier;
    return ((this.store("rolesStore")?.entities ?? []) as unknown as RoleRowLike[])
      .filter((r) => !r.deletedAt && typeof r.name === "string" && r.tier)
      .map((r) => ({
        id: String(r.id),
        name: String(r.name),
        tier: r.tier as Tier,
        allowed: !!tier && r.tier !== "admin" && RANK[r.tier as Tier] < RANK[tier],
      }))
      .sort((a, b) => RANK[a.tier] - RANK[b.tier] || a.name.localeCompare(b.name));
  }

  startGrant() {
    this.delegationEditingId = null;
    this.delegationUserId = null;
    this.delegationRoleIds = [];
    this.delegationError = null;
  }

  startEditDelegation(delegation: UserAdminDelegation) {
    this.delegationEditingId = delegation.id;
    this.delegationUserId = delegation.userId;
    this.delegationRoleIds = delegation.allowedRoles.filter((r) => !!r.name).map((r) => r.id);
    this.delegationError = null;
  }

  setDelegationUser(userId: number | null) {
    this.delegationUserId = userId;
    // Roles chosen for someone else may sit at or above this person.
    this.delegationRoleIds = [];
  }

  toggleDelegationRole(roleId: string) {
    this.delegationRoleIds = this.delegationRoleIds.includes(roleId)
      ? this.delegationRoleIds.filter((id) => id !== roleId)
      : [...this.delegationRoleIds, roleId];
  }

  get canSaveDelegation(): boolean {
    if (this.delegationSaving || this.delegationUserId == null) return false;
    const allowed = new Set(this.roleOptions.filter((r) => r.allowed).map((r) => r.id));
    return this.delegationRoleIds.every((id) => allowed.has(id));
  }

  async saveDelegation(): Promise<boolean> {
    if (!this.canSaveDelegation || this.delegationUserId == null) {
      this.delegationError = "Choose a person, and only roles below their own level.";
      return false;
    }
    this.delegationSaving = true;
    this.delegationError = null;
    try {
      const service = this.raw("user-admin-delegations");
      if (this.delegationEditingId != null) {
        await service.patch(this.delegationEditingId, { allowedRoleIds: this.delegationRoleIds });
      } else {
        await service.create({ userId: this.delegationUserId, allowedRoleIds: this.delegationRoleIds });
      }
      runInAction(() => {
        this.notice =
          this.delegationEditingId != null
            ? "Saved. The people administrator's roles are updated, on your authority."
            : "Done. They can open People now; HQ has told them.";
        this.delegationEditingId = null;
        this.delegationUserId = null;
        this.delegationRoleIds = [];
      });
      await this.refreshAfterWrite();
      return true;
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.delegationError = refusalMessage(err, "The people administrator was not saved.");
      });
      return false;
    } finally {
      runInAction(() => {
        this.delegationSaving = false;
      });
    }
  }

  get revokeTarget(): UserAdminDelegation | null {
    return this.delegations.find((d) => d.id === this.revokeTargetId) ?? null;
  }

  startRevoke(delegation: UserAdminDelegation) {
    this.revokeTargetId = delegation.id;
    this.revokeError = null;
  }

  async submitRevoke(): Promise<boolean> {
    const target = this.revokeTarget;
    if (!target) return false;
    this.revoking = true;
    this.revokeError = null;
    try {
      await this.raw("user-admin-delegations").remove(target.id);
      runInAction(() => {
        this.notice = `${target.userName ?? "They"} can no longer manage other people. Their own account is unchanged.`;
        this.revokeTargetId = null;
      });
      await this.refreshAfterWrite();
      return true;
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.revokeError = refusalMessage(err, "People administration was not revoked.");
      });
      return false;
    } finally {
      runInAction(() => {
        this.revoking = false;
      });
    }
  }

  /** Back to the constructed state on sign-out (RootStore.purgeUserData). */
  reset() {
    this.loading = false;
    this.loadError = null;
    this.logError = null;
    this.search = "";
    this.personMode = null;
    this.personTargetId = null;
    this.personForm = emptyPersonForm();
    this.personSaving = false;
    this.personError = null;
    this.personErrorDetails = [];
    this.deactivateTargetId = null;
    this.deactivating = false;
    this.deactivateError = null;
    this.clientsTargetId = null;
    this.clientsLoading = false;
    this.clientsError = null;
    this.clientToAdd = "";
    this.clientBusy = false;
    this.rowBusyId = null;
    this.rowError = null;
    this.notice = null;
    this.delegationEditingId = null;
    this.delegationUserId = null;
    this.delegationRoleIds = [];
    this.delegationSaving = false;
    this.delegationError = null;
    this.revokeTargetId = null;
    this.revoking = false;
    this.revokeError = null;
  }
}

export default PeopleStore;
